import { useState } from "react"
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
  CaretDown,
  CaretRight,
  CaretUp,
  DotsSix,
  Info,
  Plus,
  Warning,
  X,
} from "@phosphor-icons/react"

import { Button, IconButton, NumericInput, Switch } from "~/components/design-system/components"
import Select from "~/components/reusable/Select"
import { DisabledTooltip } from "~/components/reusable/DisabledTooltip"
import { usePermission } from "~/hooks/usePermission"
import { api } from "~/utils/api"
import { cn } from "~/utils/helpers"
import { formatName } from "~/utils/namingUtils"
import {
  REFERENCE_ROS_LEVELS,
  REFERENCE_ROS_LEVEL_LABELS,
  MAX_REFERENCE_ROS_MIN_LINES,
  minDaysExceedsWindow,
  type ReferenceRosLevel,
} from "~/utils/rateOfSale"
import type { RateOfSaleSettings } from "../useAllocationData"
import { ReferenceRosPreviewDialog, linesLabel } from "./ReferenceRosPreviewDialog"
import { toRateOfSaleInput } from "./setupDrafts"

type HistoryPatch = {
  minDaysWithStock?: number
  referenceRosLevels?: ReferenceRosLevel[]
  referenceRosMinLines?: number
  referenceRosPositiveOnly?: boolean
  referenceRosWarnLevel?: ReferenceRosLevel | null
}

/** Settings › Rate of sale › Too little history: the minimum days with inventory and the
 * Reference ROS chain, with what the last calculation did with the lines below the minimum. */
export function TooLittleHistoryCard({
  settings,
  edited,
  disabled,
  onCommit,
}: {
  settings: RateOfSaleSettings
  edited: boolean
  disabled: boolean
  onCommit: (patch: HistoryPatch) => void
}) {
  const [expanded, setExpanded] = useState(true)
  const [previewOpen, setPreviewOpen] = useState(false)
  const coverage = api.allocation.getReferenceRosCoverage.useQuery()
  const exceedsWindow = minDaysExceedsWindow(settings)
  const levels = settings.referenceRosLevels
  const unused = REFERENCE_ROS_LEVELS.filter((level) => !levels.includes(level))
  const belowMinimum = coverage.data?.linesBelowMinimum ?? 0
  const noRos = coverage.data?.noRos ?? 0
  const warnLevel = settings.referenceRosWarnLevel
  const warnIndex = warnLevel == null ? -1 : levels.indexOf(warnLevel)
  // The warning follows its level when the chain is reordered and is turned off when the level is removed.
  const setLevels = (referenceRosLevels: ReferenceRosLevel[]) =>
    onCommit({
      referenceRosLevels,
      ...(warnLevel != null && !referenceRosLevels.includes(warnLevel)
        ? { referenceRosWarnLevel: null }
        : {}),
    })
  const warnOptions: { value: ReferenceRosLevel | null; label: string }[] = [
    { value: null, label: "Off" },
    ...levels.map((level, index) => ({
      value: level,
      label: `Level ${index + 1}`,
    })),
  ]
  // A short move before a drag starts, so clicks on the row's buttons stay clicks.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  return (
    <div className="overflow-hidden rounded-lg border border-base bg-base">
      <div className="flex items-start gap-3 border-b border-base px-3 py-2.5">
        <div className="min-w-0 flex-1">
          <div className="paragraph-sm font-medium text-fg-base">Minimum days with inventory</div>
          <div className="paragraph-xs mt-0.5 text-fg-subtle">
            {`Below this many days w/ stock, a SKU uses Reference ROS instead of its own ${formatName("sale", true)}. Applies to every rule.`}
          </div>
        </div>
        {exceedsWindow && (
          <Warning size={14} weight="fill" className="mt-1 shrink-0 text-badge-orange-fg-text" />
        )}
        <NumericInput
          small
          requires="edit"
          disabled={disabled}
          allowZero
          value={settings.minDaysWithStock}
          onChange={(minDaysWithStock) => onCommit({ minDaysWithStock })}
          unit="days"
          containerClassName="w-28"
          className={cn("w-full text-right tabular-nums", exceedsWindow && "shadow-borders-error")}
        />
      </div>
      {exceedsWindow && (
        <div className="paragraph-xs flex items-start gap-2 border-b border-badge-orange-border bg-badge-orange-bg px-3 py-2 text-badge-orange-fg-text">
          <Warning size={12} className="mt-0.5 shrink-0" />
          <span>
            {`Minimum days with inventory (${settings.minDaysWithStock}d) is longer than the ${formatName("sale", true)} history window (${settings.windowDays}d), so no SKU could meet it. Lower it or pick a longer window to save.`}
          </span>
        </div>
      )}
      <Button
        variant="transparent"
        onClick={() => setExpanded((v) => !v)}
        className="w-full justify-start gap-2 rounded-none px-3 py-2.5 [&>div]:w-full"
        wrapperClassName="w-full"
      >
        <CaretRight size={10} className={cn("transition-transform", expanded && "rotate-90")} />
        <span className="paragraph-sm flex-1 text-left font-medium">Reference ROS</span>
        {coverage.data && !edited && (
          <span className="paragraph-xs text-fg-muted tabular-nums">
            {`${(belowMinimum - noRos).toLocaleString()} of ${linesLabel(belowMinimum)} covered`}
          </span>
        )}
      </Button>
      {expanded && (
        <div className="px-3 pb-3">
          <div className="flex items-start gap-3 pt-1 pb-2.5">
            <div className="min-w-0 flex-1">
              <p className="paragraph-xs max-w-[60ch] text-fg-subtle">
                {`A line below the minimum takes the average ROS of the first level with at least ${linesLabel(settings.referenceRosMinLines)} that have their own ROS. Fewer than that and the next level is tried. Levels are tried top to bottom.`}
              </p>
            </div>
            <NumericInput
              small
              requires="edit"
              disabled={disabled}
              min={1}
              max={MAX_REFERENCE_ROS_MIN_LINES}
              value={settings.referenceRosMinLines}
              onChange={(referenceRosMinLines) => onCommit({ referenceRosMinLines })}
              unit="lines"
              aria-label="Minimum lines per level"
              containerClassName="w-28 shrink-0"
              className="w-full text-right tabular-nums"
            />
          </div>
          <div className="flex items-start gap-3 pb-2.5">
            <div className="min-w-0 flex-1">
              <div className="paragraph-sm font-medium text-fg-base">
                Only average lines above 0
              </div>
              <p className="paragraph-xs max-w-[60ch] text-fg-subtle">
                A line whose own ROS is 0 is left out of the average and does not count toward the
                minimum lines, so a level is not pulled down by lines that never sold.
              </p>
            </div>
            <Switch
              size="xsmall"
              requires="edit"
              disabled={disabled}
              checked={settings.referenceRosPositiveOnly}
              onCheckedChange={(referenceRosPositiveOnly) => onCommit({ referenceRosPositiveOnly })}
              aria-label="Only average lines with a rate above 0"
            />
          </div>
          <div className="flex items-start gap-3 pb-2.5">
            <div className="min-w-0 flex-1">
              <div className="paragraph-sm font-medium text-fg-base">Warn from level</div>
              <p className="paragraph-xs max-w-[60ch] text-fg-subtle">
                A line that runs on this level or a later one gets a warning in Replenishment, since
                its ROS rests on a wider and wider group of lines.
              </p>
            </div>
            <div className="shrink-0">
              <Select
                requires="edit"
                isDisabled={disabled || levels.length === 0}
                value={warnOptions.find((option) => option.value === (warnLevel ?? null))}
                onChange={(option) => {
                  if (option) onCommit({ referenceRosWarnLevel: option.value })
                }}
                options={warnOptions}
                getOptionLabel={(option) => option.label}
                getOptionValue={(option) => option.value ?? "off"}
                isSearchable={false}
                size="sm"
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={({ active, over }) => {
                if (!over || active.id === over.id) return
                const from = levels.indexOf(active.id as ReferenceRosLevel)
                const to = levels.indexOf(over.id as ReferenceRosLevel)
                setLevels(arrayMove(levels, from, to))
              }}
            >
              <SortableContext items={levels} strategy={verticalListSortingStrategy}>
                {levels.map((level, index) => (
                  <SortableLevelRow
                    key={level}
                    level={level}
                    position={index + 1}
                    lines={
                      coverage.data && !edited ? (coverage.data.linesByLevel[level] ?? 0) : null
                    }
                    warns={warnIndex >= 0 && index >= warnIndex}
                    isFirst={index === 0}
                    isLast={index === levels.length - 1}
                    disabled={disabled}
                    onMove={(by) => setLevels(arrayMove(levels, index, index + by))}
                    onRemove={() => setLevels(levels.filter((other) => other !== level))}
                  />
                ))}
              </SortableContext>
            </DndContext>
            {levels.length === 0 && (
              <div className="paragraph-xs flex items-start gap-2 rounded-lg border border-badge-orange-border bg-badge-orange-bg px-3 py-2 text-badge-orange-fg-text">
                <Warning size={12} className="mt-0.5 shrink-0" />
                <span>No levels. Lines below the minimum get no ROS and no suggested qty.</span>
              </div>
            )}
          </div>
          {unused.length > 0 && (
            <div className="mt-3">
              <div className="label-sm mb-1.5 text-fg-muted">Not in the chain</div>
              <div className="flex flex-col gap-1.5">
                {unused.map((level) => (
                  <div key={level} className="flex items-center gap-3 px-3">
                    <div className="min-w-0 flex-1">
                      <div className="paragraph-xs text-fg-base">
                        {REFERENCE_ROS_LEVEL_LABELS[level].title}
                      </div>
                      <div className="paragraph-xs text-fg-muted">
                        {REFERENCE_ROS_LEVEL_LABELS[level].desc}
                      </div>
                    </div>
                    <Button
                      variant="neutral"
                      size="xs"
                      requires="edit"
                      disabled={disabled}
                      onClick={() => setLevels([...levels, level])}
                    >
                      <Plus size={12} />
                      Add
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="mt-3 flex items-center gap-3 border-t border-base pt-2.5">
            <Info size={12} className="shrink-0 text-badge-blue-fg-text" />
            <span className="paragraph-xs flex-1 text-fg-muted">
              {edited
                ? "Counts are from the last calculation. Preview lines to see your unsaved changes."
                : `${linesLabel(noRos)} below the minimum that no level covers have no ROS basis.`}
            </span>
            <DisabledTooltip
              title={
                exceedsWindow
                  ? "Fix the minimum days with inventory warning above to preview lines."
                  : undefined
              }
            >
              <Button
                variant="neutral"
                size="xs"
                disabled={exceedsWindow}
                onClick={() => setPreviewOpen(true)}
              >
                {edited ? "Preview lines" : "Show lines"}
              </Button>
            </DisabledTooltip>
          </div>
          <ReferenceRosPreviewDialog
            open={previewOpen && !exceedsWindow}
            onOpenChange={setPreviewOpen}
            input={toRateOfSaleInput(settings)}
            edited={edited}
          />
        </div>
      )}
    </div>
  )
}

function SortableLevelRow({
  level,
  position,
  lines,
  warns,
  isFirst,
  isLast,
  disabled,
  onMove,
  onRemove,
}: {
  level: ReferenceRosLevel
  position: number
  lines: number | null
  warns: boolean
  isFirst: boolean
  isLast: boolean
  disabled: boolean
  onMove: (by: -1 | 1) => void
  onRemove: () => void
}) {
  const { allowed: canEdit } = usePermission("edit")
  const draggable = !disabled && canEdit
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: level,
    disabled: !draggable,
  })

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex touch-none items-center gap-3 rounded-lg border border-base bg-base px-3 py-2",
        draggable && "cursor-grab",
        isDragging && "relative z-10 cursor-grabbing shadow-md",
      )}
      {...attributes}
      {...listeners}
      aria-label={`${REFERENCE_ROS_LEVEL_LABELS[level].title}, position ${position}. Drag to reorder.`}
    >
      <DotsSix
        size={14}
        className={cn("shrink-0", draggable ? "text-fg-muted" : "text-fg-disabled")}
      />
      <span className="paragraph-xs flex size-5.5 shrink-0 items-center justify-center rounded-full bg-badge-blue-bg font-bold text-badge-blue-fg-text">
        {position}
      </span>
      <div className="min-w-0 flex-1">
        <div className="paragraph-xs font-semibold text-fg-base">
          {REFERENCE_ROS_LEVEL_LABELS[level].title}
        </div>
        <div className="paragraph-xs text-fg-muted">{REFERENCE_ROS_LEVEL_LABELS[level].desc}</div>
      </div>
      {warns && (
        <Warning
          size={14}
          weight="fill"
          className="shrink-0 text-fg-muted"
          aria-label="Lines on this level get a level notice"
        />
      )}
      {lines != null && (
        <span className="paragraph-xs text-fg-muted tabular-nums">{linesLabel(lines)}</span>
      )}
      <div className="flex items-center gap-0.5">
        <IconButton
          variant="transparent"
          size="xs"
          requires="edit"
          allowedTitle="Try this level earlier"
          aria-label="Move up"
          disabled={disabled || isFirst}
          onClick={() => onMove(-1)}
        >
          <CaretUp size={12} />
        </IconButton>
        <IconButton
          variant="transparent"
          size="xs"
          requires="edit"
          allowedTitle="Try this level later"
          aria-label="Move down"
          disabled={disabled || isLast}
          onClick={() => onMove(1)}
        >
          <CaretDown size={12} />
        </IconButton>
        <IconButton
          variant="transparent"
          size="xs"
          requires="edit"
          allowedTitle="Remove this level from the chain"
          aria-label="Remove"
          disabled={disabled}
          onClick={onRemove}
        >
          <X size={12} />
        </IconButton>
      </div>
    </div>
  )
}
